import { setToken } from '@actions/authActions';
import { makeCurrentUser } from '@actions/userActions';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect } from 'react';
import { AuthPaths, Paths } from '@constants/paths';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUserManager } from '@components/Auth/manager';
import { currentUserSelector } from '@selectors/user';
import { MgLocalStorageKeys } from '@enums';

export const useAuth0 = () => {
  const path = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userManager = getUserManager();
  const currentUser = useSelector(currentUserSelector);

  const login = () => {
    localStorage.setItem(MgLocalStorageKeys.REDIRECT_PATH, path.pathname);
    navigate(`${Paths.AUTH}/${AuthPaths.REDIRECT}`);
  };

  const renewToken = useCallback(async () => {
    let refreshedUser;
    try {
      refreshedUser = await userManager.signinSilent();
    } catch {
      refreshedUser = null;
    }

    if (refreshedUser) {
      setToken(refreshedUser.access_token);
      dispatch(makeCurrentUser());
    } else {
      login();
    }
  }, [currentUser]);

  const checkStatus = async () => {
    try {
      const user = await userManager.getUser();
      if (user && !user.expired) {
        dispatch(setToken(user.access_token));
        dispatch(makeCurrentUser());
        return;
      }

      let shouldTryRefreshSilently = !!user;
      if (!shouldTryRefreshSilently) {
        try {
          await userManager.querySessionStatus();
          shouldTryRefreshSilently = true;
        } catch (err) {
          shouldTryRefreshSilently = false;
        }
      }
      if (shouldTryRefreshSilently) {
        try {
          await renewToken();
        } catch {
          login();
        }
      } else {
        login();
      }
    } catch (err) {
      login();
    }
  };

  useEffect(() => {
    if (
      path.pathname.includes(`${Paths.AUTH}/${AuthPaths.LOGIN}`) ||
      path.pathname.includes(`${Paths.AUTH}/${AuthPaths.REFRESH}`)
    ) {
      return;
    }
    checkStatus();
  }, []);
};
